/**
 * Camera Measurement presentation helpers.
 *
 * Format and copy only. Do not recompute P/F/S, share, or completeness from
 * seconds. Comparing O vs U is allowed only to avoid inverted ratio copy and a
 * false 100% completeness claim.
 */
import type { CameraActivityMeasurementState } from '../../services/cameraActivityMeasurementApi';
import { formatSiteTimeRange, resolveSiteTimeZone } from '../siteTime/siteTime';

export const CAMERA_MEASUREMENT_UNAVAILABLE = '—';

export const CAMERA_MEASUREMENT_STATUS_LABEL: Record<CameraActivityMeasurementState, string> = {
  MEASURED: 'Measured',
  LIMITED: 'Limited',
  NOT_MEASURED: 'Not measured',
  UNSUPPORTED: 'Not supported',
};

export const CAMERA_MEASUREMENT_REASON_COPY: Record<string, string> = {
  TELEMETRY_MISSING: 'Camera data was unavailable for part of this period.',
  NO_ANALYZABLE_TIME: 'No usable camera time was available for this period.',
  READING_SOURCE_GAP: 'Camera measurement was incomplete for this period.',
  PARTIAL_WINDOW: 'Complete measurement is not available for the selected period.',
  COVERAGE_NOT_FINAL: 'Measurement for this period is still being finalized.',
  INCOMPATIBLE_SOURCE_VERSION:
    'The measurement method changed during this period, so values are withheld.',
  SOURCE_SIGNATURE_MIXED: 'The measurement method was not consistent across this period.',
  ACTIVITY_DATA_CONFLICT: 'Measurement is unavailable because the source data is inconsistent.',
  device_scope_unresolved: 'The camera source could not be resolved to one device.',
  UNSUPPORTED_ENTITY_TYPE: 'This measurement is not supported by the current camera configuration.',
};

/** Honest LIMITED copy when activity-observed time exceeds usable camera time (O > U). */
export const CAMERA_MEASUREMENT_OU_DISAGREE_COPY =
  'Activity-observed time and usable camera time disagree. This measurement is not claim-eligible.';

export const CAMERA_MEASUREMENT_PRIVACY =
  'Area-level measurement. No person identification.';

export const CAMERA_MEASUREMENT_POSITIONING =
  'Area-level activity measured from this camera view.';

export const CAMERA_MEASUREMENT_HELPER =
  'Measures visible presence in this area during usable camera time. It does not identify people or by itself classify a safety event.';

export const MEASUREMENT_QUALITY_LABEL = 'Measurement quality';

export const SCAN_MEASUREMENT_BASIS_TITLE = 'Camera measurement during this Scan';

export const SCAN_MEASUREMENT_CLAIM_BOUNDARY =
  'Camera measurement provides context for this Scan window. It does not by itself classify a safety event.';

export const SCAN_MEASUREMENT_SIGNED_DISCLAIMER =
  'Camera measurement context is not part of the signed Scan result.';

export const SCAN_MEASUREMENT_UNAVAILABLE =
  'Measurement context unavailable. This Safety Scan remains valid without Camera Measurement context.';

export const CAMERA_AVAILABILITY_SUBTITLE =
  'Camera availability — online cameras and freshness, not Camera Measurement quality.';

export const OVERVIEW_PHYSICAL_POLICY_CLOCK_HELPER =
  'Physical activity uses a completed camera window. Policy status uses a separate current measured window.';

export const POLICY_NOW_CLOCK_LABEL = 'Current measured window';

export const SIMULTANEOUS_PRESENCE_LABEL = 'Pedestrian + forklift present';

export const SIMULTANEOUS_PRESENCE_HELPER =
  'Time when at least one pedestrian and one forklift were visible in the selected area at the same time.';

export const CAMERA_MEASUREMENT_OBSERVED_QUALIFIER = 'observed';

export const CAMERA_MEASUREMENT_OBSERVED_ONLY =
  'Values above describe observed time only.';

export function cameraMeasurementLimitedGapCopy(
  gapSeconds: number | null,
): string | null {
  if (gapSeconds == null || gapSeconds <= 0) return null;
  return `${formatCameraMeasurementDuration(gapSeconds)} of this window were not observed.`;
}

/** True when both producer observed and usable camera time are present and O > U. */
export function cameraMeasurementObservedExceedsUsable(
  observedSeconds: number | null | undefined,
  usableSeconds: number | null | undefined,
): boolean {
  if (observedSeconds == null || usableSeconds == null) return false;
  return Number(observedSeconds) > Number(usableSeconds);
}

/**
 * LIMITED quality reason copy. When O > U is a data conflict (ACTIVITY_DATA_CONFLICT
 * or producer_completeness > 1), do not let an earlier COVERAGE_NOT_FINAL hide it;
 * keep "still being finalized" if that code is also present.
 */
export function cameraMeasurementQualityReasonCopy(
  reasonCodes: string[],
  quality?: {
    source_observed_seconds?: number | null;
    usable_camera_seconds?: number | null;
    producer_completeness?: number | null;
  } | null,
): string | null {
  const codes = reasonCodes || [];
  const inverted = cameraMeasurementObservedExceedsUsable(
    quality?.source_observed_seconds,
    quality?.usable_camera_seconds,
  );
  const completeness = quality?.producer_completeness;
  const conflict = inverted && (
    codes.includes('ACTIVITY_DATA_CONFLICT')
    || (completeness != null && completeness > 1)
  );
  if (conflict) {
    const parts = [CAMERA_MEASUREMENT_OU_DISAGREE_COPY];
    if (codes.includes('COVERAGE_NOT_FINAL')) {
      parts.push(CAMERA_MEASUREMENT_REASON_COPY.COVERAGE_NOT_FINAL);
    }
    return parts.join(' ');
  }
  return cameraMeasurementReasonCopy(codes);
}

export type CameraMeasurementWindowPreset =
  | 'last_complete_15m'
  | 'last_complete_hour'
  | 'last_complete_24h';

export const CAMERA_MEASUREMENT_WINDOW_PRESETS: Array<{
  id: CameraMeasurementWindowPreset;
  label: string;
}> = [
  { id: 'last_complete_15m', label: 'Last complete 15 min' },
  { id: 'last_complete_hour', label: 'Last complete hour' },
  { id: 'last_complete_24h', label: 'Last complete 24 hours' },
];

const PT15M_MS = 15 * 60 * 1000;

export function lastCompletedUtcPt15mBoundary(now: Date): Date {
  const ms = now.getTime();
  return new Date(Math.floor(ms / PT15M_MS) * PT15M_MS);
}

export function cameraMeasurementWindowForPreset(
  preset: CameraMeasurementWindowPreset,
  now: Date = new Date(),
): { start: string; end: string } {
  const end = lastCompletedUtcPt15mBoundary(now);
  const durationMs =
    preset === 'last_complete_15m'
      ? PT15M_MS
      : preset === 'last_complete_hour'
        ? 60 * 60 * 1000
        : 24 * 60 * 60 * 1000;
  const start = new Date(end.getTime() - durationMs);
  return { start: start.toISOString(), end: end.toISOString() };
}

export function cameraMeasurementWindowLabel(preset: CameraMeasurementWindowPreset): string {
  return CAMERA_MEASUREMENT_WINDOW_PRESETS.find((item) => item.id === preset)?.label
    || 'Last complete hour';
}

export const CAMERA_MEASUREMENT_SITE_TIME_LABEL = 'Site time';

/** Compact site-local bounds. Window instants stay UTC; this is presentation only. */
export function formatCameraMeasurementWindowBounds(
  start: string,
  end: string,
  timeZone?: string | null,
): string {
  return formatSiteTimeRange(start, end, timeZone);
}

export function cameraMeasurementSiteTimeHint(timeZone?: string | null): string | null {
  return resolveSiteTimeZone(timeZone) ? CAMERA_MEASUREMENT_SITE_TIME_LABEL : null;
}

export function cameraMeasurementStatusLabel(state: string): string {
  if (state === 'MEASURED') return CAMERA_MEASUREMENT_STATUS_LABEL.MEASURED;
  if (state === 'LIMITED') return CAMERA_MEASUREMENT_STATUS_LABEL.LIMITED;
  if (state === 'NOT_MEASURED') return CAMERA_MEASUREMENT_STATUS_LABEL.NOT_MEASURED;
  if (state === 'UNSUPPORTED') return CAMERA_MEASUREMENT_STATUS_LABEL.UNSUPPORTED;
  return state.replace(/_/g, ' ');
}

export function cameraMeasurementReasonCopy(reasonCodes: string[]): string | null {
  for (const code of reasonCodes) {
    const mapped = CAMERA_MEASUREMENT_REASON_COPY[code];
    if (mapped) return mapped;
  }
  return null;
}

/**
 * Duration formatter. Null is withheld; 0 is a real measured zero.
 * Do not use falsy checks — 0 must remain 0s.
 */
export function formatCameraMeasurementDuration(seconds: number | null): string | typeof CAMERA_MEASUREMENT_UNAVAILABLE {
  if (seconds === null) return CAMERA_MEASUREMENT_UNAVAILABLE;
  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const remainder = total % 60;
  if (hours > 0) {
    const minuteLabel = String(minutes).padStart(2, '0');
    return remainder > 0 ? `${hours}h ${minuteLabel}m ${remainder}s` : `${hours}h ${minuteLabel}m`;
  }
  if (minutes > 0) {
    return remainder > 0 ? `${minutes}m ${remainder}s` : `${minutes}m`;
  }
  return `${remainder}s`;
}

/**
 * Share formatter. Uses the API share; never recomputes from seconds.
 * Null is withheld; 0 is a real measured zero.
 */
export function formatCameraMeasurementShare(share: number | null): string | typeof CAMERA_MEASUREMENT_UNAVAILABLE {
  if (share === null) return CAMERA_MEASUREMENT_UNAVAILABLE;
  if (share === 0) return '0%';
  const pct = share * 100;
  if (pct >= 1) return `${pct.toFixed(1)}%`;
  if (pct >= 0.1) return `${pct.toFixed(1)}%`;
  if (pct >= 0.01) return `${pct.toFixed(2)}%`;
  return `${pct.toFixed(3)}%`;
}

/**
 * Completeness formatter. Uses API producer_completeness; never recomputes O/U.
 * Values below 1 never render as 100%. Values above 1 are a data conflict, not 100%.
 */
export function formatCameraMeasurementCompleteness(
  completeness: number | null,
): string | typeof CAMERA_MEASUREMENT_UNAVAILABLE {
  if (completeness === null) return CAMERA_MEASUREMENT_UNAVAILABLE;
  if (!(completeness >= 0) || completeness > 1) return CAMERA_MEASUREMENT_UNAVAILABLE;
  if (completeness >= 1) return '100%';
  const pct = completeness * 100;
  if (pct >= 99.95) return `${pct.toFixed(2)}%`;
  return `${pct.toFixed(1)}%`;
}

export type CameraMeasurementIdentity =
  | { ok: true; siteId: string; deviceId: string; cameraId: string }
  | { ok: false; reason: 'missing_identity' | 'device_unresolved' };

export function resolveCameraMeasurementIdentity(args: {
  siteId?: string | null;
  cameraId?: string | null;
  deviceId?: string | null;
  inventoryDeviceId?: string | null;
}): CameraMeasurementIdentity {
  const siteId = String(args.siteId || '').trim();
  const cameraId = String(args.cameraId || '').trim();
  if (!siteId || !cameraId) {
    return { ok: false, reason: 'missing_identity' };
  }
  const deviceId = String(args.deviceId || '').trim()
    || String(args.inventoryDeviceId || '').trim();
  if (!deviceId) {
    return { ok: false, reason: 'device_unresolved' };
  }
  return { ok: true, siteId, deviceId, cameraId };
}

export function isMeasuredZero(seconds: number | null, share: number | null): boolean {
  return seconds === 0 && share === 0;
}
